import React, { useEffect, useState } from "react";
import { Button, Form, Header, Input, Message, Segment, Dropdown } from "semantic-ui-react";
import { toast } from "react-toastify";
import { ToasterConfig } from "../../../config/ToasterConfig";
import { EProductType } from "../../../models/requests/e-product-type";
import {CreateProductCommand, UpdateProductCommand} from "../../../models/requests/product-filter";
import useProductService from "../../../services/backoffice/hooks/useProductsService";

interface ProductFormProps {
    productId?: string;
    onClose?: () => void;
    isModal?: boolean;
    open?: boolean;
}

const ProductForm: React.FC<ProductFormProps> = ({ productId, onClose }) => {
    const [product, setProduct] = useState<Partial<CreateProductCommand>>({ type: EProductType.DryLoad });
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const { createProduct, updateProduct, getProductById } = useProductService();

    const productTypeOptions = [
        { key: "dryLoad", text: "Carga seca", value: EProductType.DryLoad },
        { key: "bulk", text: "Granéis", value: EProductType.Bulk },
        { key: "other", text: "Outros", value: EProductType.Other }
    ];

    useEffect(() => {
        if (productId) {
            setLoading(true);
            getProductById(productId)
                .then((response) => {
                    setProduct({
                        name: response.name,
                        description: response.description,
                        code: response.code,
                        type: response.type,
                    });
                })
                .catch(() => {
                    setError("Erro ao carregar o produto.");
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [productId]);

    const handleSubmit = async () => {
        try {
            if (!product.name || !product.code || !product.description) {
                setError("Todos os campos são obrigatórios.");
                return;
            }

            setLoading(true);
            const command: CreateProductCommand | UpdateProductCommand = {
                name: product.name,
                description: product.description,
                code: product.code,
                type: product.type || EProductType.DryLoad
            };

            if (productId) {
                await updateProduct(productId, command as UpdateProductCommand);
                toast.success("Produto atualizado com sucesso", ToasterConfig.success);
            } else {
                await createProduct(command);
                toast.success("Produto criado com sucesso", ToasterConfig.success);
            }

            if (onClose) onClose();
        } catch {
            setError("Erro ao salvar o produto.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setProduct((prev) => ({ ...prev, [name]: value }));
    };

    const handleDropdownChange = (event: React.SyntheticEvent, data: any) => {
        setProduct((prev) => ({ ...prev, type: data.value }));
    };

    const handleClear = () => {
        setProduct({ type: EProductType.DryLoad });
    };

    const isSubmitDisabled = !product.name || !product.code || !product.description;

    return (
        <Segment raised style={{ maxWidth: "880px", margin: "5px auto", padding: "20px" }}>
            <Header
                as="h3"
                style={{
                    background: "#49ab3a",
                    color: "#fff",
                    display: "flex",
                    alignItems: "left",
                    justifyContent: "space-between",
                    padding: "10px",
                    margin: "0",
                    borderRadius: "5px 5px 0 0",
                }}
            >
                {productId ? "Editar Produto" : "Cadastro de Produto"}
            </Header>
            <Form onSubmit={handleSubmit}>
                <div style={{ border: "2px solid #d4d4d5", borderRadius: "4px", padding: "10px", margin: "10px 0" }}>
                    <Form.Group widths="equal">
                        <Form.Field required>
                            <label>Código</label>
                            <Input
                                name="code"
                                placeholder="Digite o código"
                                value={product.code || ""}
                                onChange={handleInputChange}
                                disabled={loading}
                                style={{ padding: "10px", width: "100%" }}
                                fluid
                                error={isSubmitDisabled && !product.code}
                            />
                        </Form.Field>
                        <Form.Field required>
                            <label>Nome</label>
                            <Input
                                name="name"
                                placeholder="Digite o nome"
                                value={product.name || ""}
                                onChange={handleInputChange}
                                disabled={loading}
                                style={{ padding: "10px", width: "100%" }}
                                fluid
                                error={isSubmitDisabled && !product.name}
                            />
                        </Form.Field>
                        <Form.Field required>
                            <label>Descrição</label>
                            <Input
                                name="description"
                                placeholder="Digite a descrição"
                                value={product.description || ""}
                                onChange={handleInputChange}
                                disabled={loading}
                                style={{ padding: "10px", width: "100%" }}
                                fluid
                                error={isSubmitDisabled && !product.description}
                            />
                        </Form.Field>
                        <Form.Field required>
                            <label>Tipo de Produto</label>
                            <Dropdown
                                name="type"
                                options={productTypeOptions}
                                value={product.type}
                                onChange={handleDropdownChange}
                                disabled={loading}
                                placeholder="Selecione o tipo de produto"
                                selection
                                fluid
                            />
                        </Form.Field>
                    </Form.Group>
                </div>
                <div style={{ textAlign: "right" }}>
                    <Button color="green" type="submit" disabled={isSubmitDisabled || loading}>
                        {productId ? "Atualizar" : "Salvar"}
                    </Button>
                    <Button type="button" onClick={handleClear} disabled={loading}>
                        Limpar
                    </Button>
                    <Button type="button" onClick={onClose} disabled={loading}>
                        Cancelar
                    </Button>
                </div>
                {error && <Message negative>{error}</Message>}
            </Form>
        </Segment>
    );
};

export default ProductForm;
